import React from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import Spinner from "../../../../../atoms/Spinner/Spinner";
import { AssessmentPagination } from "../../../../../types/goal";
import "./AssessmentPageControls.scss";

interface AssessmentPageControlsProps {
  pagination?: AssessmentPagination;
  isLoading?: boolean;
  isInitialLoad?: boolean;
  onPageChange: (page: number) => void;
  children: React.ReactNode;
}

const defaultPagination: AssessmentPagination = {
  page: 1,
  per_page: 3,
  totalRecords: 0,
  totalPages: 0,
};

const AssessmentPageControls: React.FC<AssessmentPageControlsProps> = ({
  pagination,
  isLoading,
  isInitialLoad,
  onPageChange,
  children,
}) => {
  const {
    page = 1,
    per_page = 3,
    totalRecords = 0,
    totalPages = 0,
  } = pagination ?? defaultPagination;

  const startRecord = Math.min((page - 1) * per_page + 1, totalRecords);
  const endRecord = Math.min(page * per_page, totalRecords);
  const canGoOlder = page < totalPages && !isLoading;
  const canGoNewer = page > 1 && !isLoading;
  const showOverlay = isLoading && !isInitialLoad;

  return (
    <div className="assessment-page-controls">
      <div className="assessment-page-controls__meta">
        {totalRecords > 0 ? (
          <span>
            Assessments {startRecord}–{endRecord} of {totalRecords}
            {totalPages > 1 && ` · Page ${page} of ${totalPages}`}
          </span>
        ) : isLoading ? (
          <span>Loading assessments...</span>
        ) : (
          <span>No assessments</span>
        )}
      </div>

      <div className="assessment-page-controls__grid-row">
        <button
          type="button"
          className="assessment-page-controls__nav-btn assessment-page-controls__nav-btn--left"
          disabled={!canGoNewer}
          onClick={() => onPageChange(page - 1)}
          aria-label="Show newer assessments"
          title="Newer assessments"
        >
          <LeftOutlined />
        </button>

        <div
          className={`assessment-page-controls__grid-slot${
            showOverlay ? " loading" : ""
          }${isInitialLoad ? " assessment-page-controls__grid-slot--initial-loading" : ""}`}
        >
          {children}
          {showOverlay && (
            <div className="assessment-page-controls__grid-overlay">
              <Spinner size="medium" color="#299D6B" />
            </div>
          )}
        </div>

        <button
          type="button"
          className="assessment-page-controls__nav-btn assessment-page-controls__nav-btn--right"
          disabled={!canGoOlder}
          onClick={() => onPageChange(page + 1)}
          aria-label="Show older assessments"
          title="Older assessments"
        >
          <RightOutlined />
        </button>
      </div>
    </div>
  );
};

export default AssessmentPageControls;
